import * as THREE from "three";
import type { ActorRefFrom } from "xstate";
import { resolveAttackRoll } from "./attackResolver.js";
import { CombatEndWatcher } from "./CombatEndWatcher.js";
import { startleTimerSeconds } from "./startleTimer.js";
import { isPeaceMode, scaleEnemyDamage, scaleEnemyCooldown } from "./difficulty.js";
import { conditionManager } from "./ConditionManager.js";
import type { CleanseTrigger } from "../types/condition.js";
import {
  applyConditionTo,
  conditionCooldownScale,
  isIncapacitated,
  wakeOnDamage,
} from "./conditionRuntime.js";
import { loadSpell } from "../loaders/SpellLoader.js";
import { randomFloat01 } from "./dice.js";
import { applyDamage, resolveDamage } from "./damageResolver.js";
import { openReactionWindow } from "./reactionQueue.js";
import { matchReactions, type ReactionTrigger } from "./ReactionSystem.js";
import { EventBus } from "../EventBus.js";
import { pushFeed, formatAttackHit, formatAttackMiss, formatCombatEnded } from "../Feed.js";
import { PartyStore } from "../../store/PartyStore.js";
import { WorldStore } from "../../store/WorldStore.js";
import { LootPileStore } from "../../store/LootPileStore.js";
import type { LootPile } from "../inventory/LootPile.js";
import { gameActor } from "../../ui/hooks/useGameMachine.js";
import { abilityModifier } from "../types/entity.js";
import type {
  Character,
  EnemyFaction,
  MonsterEntity,
  MonsterSize,
  PlayerCharacter,
} from "../types/entity.js";
import { canJoinCell } from "../grid/packRules.js";
import type { DamageType } from "../types/combat.js";
import type { AbilityName } from "../types/entity.js";
import type { EquipmentSlot, Weapon } from "../types/items.js";
import { InventoryStore } from "../../store/InventoryStore.js";
import { Direction, type Position } from "../types/grid.js";
import type { CombatEvent } from "../types/combat.js";
import type { playerMachine } from "../../machines/playerMachine.js";
import type { enemyMachine } from "../../machines/enemyMachine.js";
import type { SpriteManager } from "../sprites/SpriteManager.js";
import type { GridMap } from "../grid/GridMap.js";
import { hasLineOfSight } from "../grid/LineOfSight.js";
import { findPath } from "../grid/Pathfinder.js";
import { cellOccupancyStore } from "../grid/cellOccupancyStore.js";
import { cooldownClock } from "./CooldownClock.js";
import { chooseNextPatrolStep } from "../ai/patrol.js";
import { manhattan } from "../grid/distance.js";
import { aggregateAttackModifiers } from "./conditionModifiers.js";
import { rollDiceNotation } from "./dice.js";
import type { Spell } from "../types/spells.js";
import { hasSlot, consumeSlot } from "../spells/SpellSlots.js";
import {
  beginSpellCooldown,
  isSpellCooldownActive,
  spellCooldownSeconds,
} from "../spells/spellCooldown.js";
import { resolveSpellAttack, resolveSavingThrow, spellSaveDc } from "../spells/spellResolvers.js";
import { CLASS_PRIMARY_ABILITY, type SrdClassSlug } from "../types/srdClasses.js";
import type { ResolvedTarget } from "../spells/SpellTargetingDispatcher.js";
import type { ConditionId } from "../types/entity.js";
import {
  getEnemyAttackProfile,
  selectAttackTargets,
  type TargetingMember,
} from "../ai/targetSelection.js";

// ── Constants ────────────────────────────────────────────────────────────────

/** Manhattan-distance threshold for IDLE → COMBAT transition. */
const DETECTION_RANGE = 5;

/** Seconds between enemy actions (ATB). */
const ENEMY_ACTION_COOLDOWN = 3.0;

/**
 * Base seconds between player attacks (ATB). B11 wires this through
 * `cooldownClock` per character. Future modifiers (DEX scaling, Slow/Haste
 * conditions) plug into `effectivePlayerCooldown` so the multiplier story
 * stays in one place.
 */
const PLAYER_ACTION_COOLDOWN = 3.0;

/** B11 — `cooldownClock` key for a character's attack timer. */
function playerAtbTimerId(instanceId: string): string {
  return `player:atk:${instanceId}`;
}

/**
 * B11 — effective attack cooldown for `member`. v1 returns the flat base;
 * future tasks layer in DEX scaling (high-DEX rogue swings faster) and
 * status conditions (`Slow` 2×, `Haste` 0.5×) here so the resolution
 * happens in one spot.
 */
function effectivePlayerCooldown(member: PlayerCharacter): number {
  // EV-E4: Haste ×0.5 / Slow ×2 plug in here as combat-physical planned.
  return PLAYER_ACTION_COOLDOWN * conditionCooldownScale(member.instanceId);
}

/**
 * B09: seconds of "no engaged enemy" before combat auto-ends. Gives fleeing
 * enemies a beat to drop out without flickering the HUD.
 */
const COMBAT_END_QUIET_WINDOW = 3.0;

/**
 * B08: Manhattan distance at which an engaged enemy gives up the chase
 * and drops back to ALERT (which calms down to IDLE per E04). Must
 * exceed DETECTION_RANGE so moving out of combat doesn't immediately
 * re-engage.
 */
const FLEE_DISTANCE = 10;

// ── ATB snapshot types (consumed by the HUD) ─────────────────────────────────

export interface AtbSnapshot {
  /** Seconds remaining until the action fires. 0 = ready. */
  readonly remaining: number;
  /** Full cooldown duration in seconds (so the HUD can compute a fill %). */
  readonly duration: number;
}

export interface EnemyAtbSnapshot extends AtbSnapshot {
  readonly instanceId: string;
  /** True when the enemy is currently in COMBAT/ATTACKING state. */
  readonly engaged: boolean;
  /**
   * Faction at snapshot time — drives the HUD colour on the floating
   * cooldown bar (`hostile` → red, `neutral` → yellow, `friendly` → blue).
   */
  readonly faction: EnemyFaction;
  /**
   * Live world-space sprite position, or null when no SpriteManager is
   * wired (pre-E3 tests, dispose, etc.). Consumed by `EnemyMeter` to
   * project floating cooldown bars to screen coordinates.
   */
  readonly worldPos: { readonly x: number; readonly y: number; readonly z: number } | null;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface EnemyRecord {
  entity: MonsterEntity;
  actor: ActorRefFrom<typeof enemyMachine>;
  actionTimer: number;
  /**
   * Seconds until the next engaged-movement step. Split from
   * `actionTimer` so closing distance and swinging are independent
   * cadences — an enemy no longer spends its attack action walking,
   * and vice versa.
   */
  moveTimer: number;
  /** Duration the current attack cooldown started from (HUD fill %). */
  actionDuration: number;
  /** Seconds until the next patrol-step roll while IDLE/PATROLLING (E06). */
  patrolTimer: number;
  /**
   * Seconds left in the ALERT/ALERTED watchful window. Set when the
   * enemy loses the player (PLAYER_LOST → alert); ticked down in
   * `tick()` and fires ALERT_TIMEOUT / ALERTED_TIMEOUT back to idle so
   * the enemy resumes patrolling. Without this driver the alert states
   * are roach motels — nothing else ever sends the timeout events, so
   * every enemy that ever lost the player froze in place permanently.
   */
  alertTimer: number;
  /** Three.js placeholder mesh on Layer 1 (removed on death). null when using SpriteManager. */
  placeholder: THREE.Mesh | null;
  /** E05: true once this enemy has ever detected the player; used to pick startle window. */
  hasEverDetected: boolean;
}

/** Seconds between patrol-step rolls. Tune-ready — balance lives on one line. */
const PATROL_STEP_INTERVAL = 1.5;

/**
 * Seconds between engaged-movement steps. Separate from the attack
 * cooldown (`ENEMY_ACTION_COOLDOWN`): before the split, every step
 * consumed a whole 3s attack action, making chases glacial; after it,
 * enemies close at a walk while their swing charges independently.
 */
const ENEMY_MOVE_COOLDOWN = 2.0;

/**
 * Grace clamp: when an enemy's attack comes off cooldown while it
 * still can't reach or see the party, the timer holds here instead of
 * zero — so closing into range gives the player a beat of telegraph
 * rather than an instant hit on arrival.
 */
const ATTACK_READY_GRACE = 0.75;

/** Seconds an enemy stays watchful in ALERT/ALERTED before calming back to idle (E04). */
const ALERT_CALM_SECONDS = 6;

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Read the optional `conditions` set off any entity type. `Character`
 * carries it; the pre-C01 `PlayerCharacter` stub and `MonsterEntity` do
 * not (yet). Empty iterable → zero stat hooks contributed.
 */
function entityConditions(entity: object): Iterable<ConditionId> {
  // EV-E4: conditions live in the conditionManager singleton keyed by
  // instanceId — the legacy per-entity Set was never mutated.
  const id = (entity as { instanceId?: string }).instanceId;
  if (!id) return [];
  return conditionManager.list(id).map((c) => c.id);
}

let _uidCounter = 0;
function uid(): string {
  return `${Date.now()}-${(_uidCounter++).toString(36)}`;
}

/**
 * Narrow `PlayerCharacter` into the minimal shape the target-selector needs.
 * Picks up `formationSlot` when the concrete runtime value is a `Character`
 * (C05+); falls back to null for the MVP hardcoded single-member party.
 */
function toTargetingMembers(members: readonly PlayerCharacter[]): readonly TargetingMember[] {
  return members.map((m) => {
    const slot = "formationSlot" in m ? ((m as Character).formationSlot ?? null) : null;
    return { instanceId: m.instanceId, hp: m.hp, formationSlot: slot };
  });
}

// ── CombatManager ─────────────────────────────────────────────────────────────

/**
 * ATB-driven combat orchestrator.
 *
 * Responsibilities:
 *  - Tracks an actionTimer per enemy; fires enemy attacks when it expires.
 *  - Detects player proximity and transitions enemy actors IDLE → COMBAT.
 *  - Moves enemies one step toward the player each ATB cycle via A*.
 *  - Exposes `playerAttack()` for the canvas click handler.
 *  - Emits EventBus events consumed by the React UI (CombatLog, ReactionPrompt).
 */
export class CombatManager {
  private readonly enemies = new Map<string, EnemyRecord>();
  // E15: occupancy is now a module singleton so the collision detector,
  // spawn paths, and the AI pathfinder all read/write the same source.
  private readonly occupancy = cellOccupancyStore;
  private playerActor: ActorRefFrom<typeof playerMachine> | null = null;
  private playerPosition: Position = { x: 0, z: 0 };
  private playerFacing: Direction = Direction.South;
  private inCombat = false;
  private readonly endWatcher = new CombatEndWatcher(COMBAT_END_QUIET_WINDOW);

  // Optional E3 scene references (null = E2 fallback mode)
  private spriteMgr: SpriteManager | null = null;
  private gridMap: GridMap | null = null;

  // E12: dependency-injected loot resolver. Returning null means "no
  // drop"; returning a pile hands it to `LootPileStore`. Resolver sits
  // outside the manager because item + table lookup is owned by the
  // runtime (main.tsx) which has access to DataRegistry.
  private lootResolver: ((entity: MonsterEntity) => LootPile | null) | null = null;

  // ── Wiring ────────────────────────────────────────────────────────────────

  /**
   * Wire in SpriteManager and GridMap after map load (E3+).
   * Without this call the manager falls back to E2 placeholder-mesh behaviour.
   */
  setScene(spriteMgr: SpriteManager, gridMap: GridMap): void {
    this.spriteMgr = spriteMgr;
    this.gridMap = gridMap;
  }

  /**
   * E12: wire in a loot resolver. Called once the runtime has a
   * `DataRegistry` and item lookup; absent in tests. Pass `null` to
   * detach (map teardown).
   */
  setLootResolver(resolver: ((entity: MonsterEntity) => LootPile | null) | null): void {
    this.lootResolver = resolver;
  }

  // ── Registration ──────────────────────────────────────────────────────────

  registerEnemy(
    entity: MonsterEntity,
    actor: ActorRefFrom<typeof enemyMachine>,
    placeholder: THREE.Mesh | null,
  ): void {
    this.enemies.set(entity.instanceId, {
      entity,
      actor,
      actionTimer: ENEMY_ACTION_COOLDOWN,
      moveTimer: ENEMY_MOVE_COOLDOWN,
      actionDuration: ENEMY_ACTION_COOLDOWN,
      patrolTimer: PATROL_STEP_INTERVAL,
      alertTimer: 0,
      placeholder,
      hasEverDetected: false,
    });
    this.occupancy.place(entity.instanceId, entity.position);
    // E-pack: co-spawned pack members share the cell — slot them into
    // their 2×2 corners as each registers.
    this.syncCellSlots(entity.position);

    // EV-E3: resolve this caster's spell kit into the cache so READY
    // actions can pick synchronously. Fire-and-forget; until loaded the
    // enemy simply fights with its weapon.
    if (entity.spells.length > 0) void this.warmEnemySpells(entity.dataId, entity.spells);

    // A01b — mirror enemyMachine state transitions onto the EventBus so
    // SpriteManager can swap animation loops without holding a direct
    // reference back into combat. The subscription lives as long as the
    // actor itself; dispose happens via the enemy's own lifecycle.
    let lastState = String(actor.getSnapshot().value);
    EventBus.emit("enemy:state_changed", { instanceId: entity.instanceId, state: lastState });
    actor.subscribe((snap) => {
      const next = String(snap.value);
      if (next === lastState) return;
      lastState = next;
      EventBus.emit("enemy:state_changed", { instanceId: entity.instanceId, state: next });
    });
  }

  /**
   * E07: promote a registered neutral/friendly entity to hostile and
   * pull it into combat after a "drawing weapons" pause. Idempotent —
   * re-calling on an already-hostile record is a no-op. Returns true
   * when a flip happened.
   *
   * Runtime path: Ink `set_hostile`, pickpocket-caught, theft detection.
   * Each of those sources routes through here so the feed line, event,
   * and startle delay stay in one place.
   */
  setHostile(instanceId: string, action?: string): boolean {
    const record = this.enemies.get(instanceId);
    if (!record || record.entity.faction === "hostile") return false;

    const previous = record.entity.faction;
    (record.entity as { faction: EnemyFaction }).faction = "hostile";

    // Peace mode: the faction flip and its event still land (the rim
    // recolors, so an Ink `set_hostile` knot is visibly verifiable) but
    // the combat handoff is skipped — nothing engages.
    if (!isPeaceMode()) {
      // Drawing-weapons pause before first swing — reuses the re-engage
      // startle window (1–2s).
      record.actionTimer = startleTimerSeconds({
        firstContact: false,
        rand: randomFloat01,
      });
      record.hasEverDetected = true;
      record.actor.send({ type: "PLAYER_DETECTED" });

      if (!this.inCombat) {
        this.inCombat = true;
        this.endWatcher.reset();
        gameActor.send({ type: "COMBAT_STARTED" });
      }
    }

    EventBus.emit("enemy:faction_flipped", {
      instanceId,
      name: record.entity.name,
      previous,
      next: "hostile",
      ...(action !== undefined ? { action } : {}),
    });
    return true;
  }

  /**
   * Read-only view of the cell-occupancy index (E09). Returns the set
   * of enemy instanceIds currently on `pos`. Callers should treat the
   * result as an immutable snapshot.
   */
  enemiesAt(pos: Position): readonly string[] {
    return this.occupancy.at(pos);
  }

  /** True if any enemy occupies `pos`. */
  isCellOccupied(pos: Position): boolean {
    return this.occupancy.isOccupied(pos);
  }

  registerPlayer(party: PlayerCharacter[], actor: ActorRefFrom<typeof playerMachine>): void {
    PartyStore.setMembers(party);
    this.playerActor = actor;
    this.playerFacing = actor.getSnapshot().context.facing;
    actor.subscribe((snap) => {
      this.playerFacing = snap.context.facing;
    });
  }

  updatePlayerPosition(pos: Position): void {
    this.playerPosition = pos;
  }

  /**
   * Cell directly in front of the party (one step along current facing).
   * Used for melee reachability — melee attacks can only hit an enemy
   * whose cell equals frontCell(). Ranged weapons + spells ignore this.
   */
  private frontCell(): Position {
    const f = this.playerFacing;
    const dx = f === Direction.East ? 1 : f === Direction.West ? -1 : 0;
    const dz = f === Direction.South ? 1 : f === Direction.North ? -1 : 0;
    return { x: this.playerPosition.x + dx, z: this.playerPosition.z + dz };
  }

  /** Weapon equipped in `hand` (defaults to mainHand), or null when unarmed. */
  private equippedWeapon(
    attacker: PlayerCharacter,
    hand: EquipmentSlot | undefined,
  ): Weapon | null {
    const inv = InventoryStore.get(attacker.instanceId);
    const slot = hand ?? "mainHand";
    const equipped = inv.equipment[slot] ?? inv.equipment.mainHand;
    return isWeapon(equipped) ? equipped : null;
  }

  /**
   * True when the attacker's chosen weapon is melee — i.e. has no
   * "ranged" property. Unarmed strikes count as melee.
   */
  private isMeleeAttack(attacker: PlayerCharacter, hand: EquipmentSlot | undefined): boolean {
    const weapon = this.equippedWeapon(attacker, hand);
    if (!weapon) return true;
    return !weapon.properties.includes("ranged");
  }

  /**
   * Reachability gate. Melee attacks only reach the cell directly in
   * front of the party; ranged attacks have no distance gate but are
   * line-of-sight-gated in `playerAttackNearest`.
   */
  private isReachable(
    attacker: PlayerCharacter,
    targetPos: Position,
    hand: EquipmentSlot | undefined,
  ): boolean {
    if (!this.isMeleeAttack(attacker, hand)) return true;
    const front = this.frontCell();
    return targetPos.x === front.x && targetPos.z === front.z;
  }

  // ── Player attack (called from canvas click via Raycaster or hand buttons) ──

  /**
   * Attack the nearest living enemy. Used by U21 hand buttons and by the
   * canvas-click "no specific target" fallback.
   */
  playerAttackNearest(opts?: PlayerAttackOpts): void {
    // Pick the attacker first so we can honour their weapon's reach.
    const explicit = opts?.attackerInstanceId
      ? PartyStore.getMembers().find((m) => m.instanceId === opts.attackerInstanceId && m.hp > 0)
      : undefined;
    const attacker = explicit ?? this.firstReadyAttacker();
    if (!attacker) return;

    const melee = this.isMeleeAttack(attacker, opts?.hand);
    const front = melee ? this.frontCell() : null;

    let closest: string | null = null;
    let closestDist = Infinity;
    for (const [id, record] of this.enemies) {
      if (record.entity.hp <= 0) continue;
      const pos = record.entity.position;
      if (front && (pos.x !== front.x || pos.z !== front.z)) continue;
      // Ranged shots hit anything visible, but never through walls —
      // without this the hand buttons snipe the manhattan-nearest enemy
      // anywhere on the map (Bly, from the pool terrace, through five
      // walls).
      if (
        !melee &&
        this.gridMap &&
        !hasLineOfSight(this.gridMap, this.playerPosition, pos)
      ) {
        continue;
      }
      const d = manhattan(pos, this.playerPosition);
      if (d < closestDist) {
        closestDist = d;
        closest = id;
      }
    }
    if (closest !== null) {
      this.playerAttack(closest, { ...opts, attackerInstanceId: attacker.instanceId });
    } else if (melee) {
      pushFeed(`${attacker.name} has no melee target in reach.`);
    } else {
      pushFeed(`${attacker.name} has no target in sight.`);
    }
  }

  /**
   * B11 — pick the first alive AND off-cooldown party member. The canvas-
   * click fallback uses this so spam-clicks don't all resolve through one
   * character. Returns null when everyone is on cooldown or down.
   */
  private firstReadyAttacker(): PlayerCharacter | null {
    for (const m of PartyStore.getMembers()) {
      if (m.hp <= 0) continue;
      if (cooldownClock.isActive(playerAtbTimerId(m.instanceId))) continue;
      return m;
    }
    return null;
  }

  playerAttack(targetInstanceId: string, opts?: PlayerAttackOpts): void {
    const record = this.enemies.get(targetInstanceId);
    if (!record || record.entity.hp <= 0) return;

    const members = PartyStore.getMembers();
    // B11: explicit attacker must still be off cooldown; canvas-click
    // fallback picks the first alive AND ready member.
    let attacker: PlayerCharacter | undefined;
    if (opts?.attackerInstanceId) {
      const m = members.find((m) => m.instanceId === opts.attackerInstanceId && m.hp > 0);
      if (m && cooldownClock.isActive(playerAtbTimerId(m.instanceId))) return;
      attacker = m;
    } else {
      attacker = this.firstReadyAttacker() ?? undefined;
    }
    if (!attacker) return;

    // EV-E4: incapacitated members (Sleep, paralysis, …) can't attack.
    if (isIncapacitated(attacker.instanceId)) {
      pushFeed(`${attacker.name} can't act!`);
      return;
    }

    // Range gate: melee weapons only reach the cell directly in front of
    // the party. Ranged weapons ignore the gate (LOS handled elsewhere).
    if (!this.isReachable(attacker, record.entity.position, opts?.hand)) {
      pushFeed(`${attacker.name} cannot reach ${record.entity.name}.`);
      return;
    }

    // B11: kick the per-character cooldown before resolving the attack so
    // the HUD bar drains immediately on tap. The clock self-clears on expiry.
    const atbId = playerAtbTimerId(attacker.instanceId);
    cooldownClock.register(atbId, effectivePlayerCooldown(attacker), () => {});

    const profile = resolveAttackProfile(attacker, opts?.hand);
    const abilityMod = abilityModifier(attacker.abilities[profile.ability]);

    const mods = aggregateAttackModifiers({
      attacker: entityConditions(attacker),
      defender: entityConditions(record.entity),
    });
    const attackRoll = resolveAttackRoll({
      abilityModifier: abilityMod,
      proficiencyBonus: attacker.proficiencyBonus,
      targetAC: record.entity.armorClass,
      hitBonus: mods.hitBonus,
    });

    const combatEvent: CombatEvent = {
      attackerId: attacker.instanceId,
      targetId: record.entity.instanceId,
      attackRoll,
      damage: null,
      hit: attackRoll.hit,
      timestamp: Date.now(),
    };

    if (attackRoll.hit) {
      const dmg = resolveDamage({
        diceCount: profile.diceCount,
        diceSides: profile.diceSides,
        modifier: abilityMod + mods.damageBonus,
        damageType: profile.damageType,
        isCriticalHit: attackRoll.isCriticalHit,
      });

      const applied = applyDamage(record.entity, dmg.total, dmg.damageType);
      record.entity.hp = applied.newHp;
      record.actor.send({ type: "TAKE_DAMAGE", amount: applied.appliedAmount });
      wakeOnDamage(record.entity.instanceId);
      EventBus.emit("combat:damage_taken", {
        instanceId: record.entity.instanceId,
        amount: applied.appliedAmount,
        damageType: dmg.damageType,
      });

      pushFeed(
        formatAttackHit(
          attacker.name,
          record.entity.name,
          dmg.total,
          dmg.damageType,
          attackRoll.isCriticalHit,
        ),
      );

      (combatEvent as { -readonly [K in keyof CombatEvent]: CombatEvent[K] }).damage = dmg;

      if (record.entity.hp <= 0) {
        this.onEnemyDied(record);
      }
    } else {
      pushFeed(formatAttackMiss(attacker.name, record.entity.name, attackRoll.isCriticalMiss));
    }

    EventBus.emit("combat:attack", combatEvent);
  }

  // ── Player cast (S22) ────────────────────────────────────────────────────

  /**
   * Cast a spell from a party member. Routes by `spell.attackType`:
   *   - "auto"          → guaranteed hit, deals damage to each picked target
   *                       (Magic Missile: N darts, one per nearest enemy)
   *   - "saving_throw"  → each target rolls a save vs the caster's DC,
   *                       half damage on success when `halfOnSave`
   *   - "attack_roll"   → standard d20 to-hit vs target AC
   *   - "none"          → no roll; applied as heal (when `spell.heal` set)
   *
   * Targeting:
   *   - `target == null` triggers the auto-target heuristic (nearest
   *     enemy for damage, most-wounded ally for heals). Used by Magic
   *     Missile and Cure Wounds in v1; Fireball needs an explicit `aoe`
   *     target from S23 (target picker).
   *
   * Slot is consumed up front. Spell-ATB cooldown is registered after a
   * successful resolution. `combat:spell_cast` fires on every successful
   * cast so the VFX layer (S24) can paint regardless of hit/miss/save.
   */
  castSpell(opts: {
    casterId: string;
    spell: Spell;
    slotLevel: number;
    target?: ResolvedTarget;
  }): void {
    const caster = PartyStore.getMember(opts.casterId);
    if (!caster || caster.hp <= 0) return;
    if (isSpellCooldownActive(cooldownClock, caster.instanceId)) return;
    // EV-E4: incapacitation blocks all actions; Silence blocks casting.
    if (isIncapacitated(caster.instanceId)) {
      pushFeed(`${caster.name} can't act!`);
      return;
    }
    if (conditionManager.has(caster.instanceId, "silence")) {
      pushFeed(`${caster.name} is silenced and cannot cast!`);
      return;
    }

    // Slot accounting (cantrips skip).
    if (opts.spell.level > 0) {
      if (!hasSlot(caster, opts.slotLevel)) return;
      const next = consumeSlot(caster, opts.slotLevel);
      PartyStore.updateMember(caster.instanceId, { spellSlots: next.spellSlots });
    }

    // EV-E7: custom classes carry their casting stat on the character;
    // legacy slugs fall back to the hardcoded table.
    const ability =
      (caster as { castingAbility?: AbilityName }).castingAbility ??
      CLASS_PRIMARY_ABILITY[caster.characterClass as SrdClassSlug] ??
      "INT";
    const spellMod = abilityModifier(caster.abilities[ability]);
    const dc = spellSaveDc(spellMod, caster.proficiencyBonus);

    const resolvedTarget = opts.target ?? this.autoTarget(opts.spell, caster);
    if (!resolvedTarget) {
      pushFeed(`${caster.name} found no target.`);
      return;
    }

    const targets: string[] = [];
    let cellTarget: { x: number; z: number } | null = null;

    if (opts.spell.heal) {
      // Heal path (Cure Wounds). Target id comes from the resolved target.
      const allyId =
        resolvedTarget.kind === "single" || resolvedTarget.kind === "touch"
          ? resolvedTarget.instanceId
          : resolvedTarget.kind === "self"
            ? resolvedTarget.casterId
            : null;
      if (allyId) {
        const heal = rollDiceNotation(opts.spell.heal.dice) + spellMod;
        const before = PartyStore.getMember(allyId);
        if (before) {
          const healed = Math.min(before.maxHp, before.hp + Math.max(1, heal));
          PartyStore.updateMember(allyId, { hp: healed });
          targets.push(allyId);
          if (healed - before.hp > 0) {
            EventBus.emit("combat:healed", { instanceId: allyId, amount: healed - before.hp });
          }
          pushFeed(
            `${caster.name} casts ${opts.spell.name} on ${before.name} (+${String(healed - before.hp)} HP).`,
          );
        }
      }
    } else if (opts.spell.damage) {
      // Damage paths.
      const damageType = opts.spell.damage.type;
      const damageDice = opts.spell.damage.dice;

      const enemyTargets = this.expandDamageTargets(opts.spell, resolvedTarget);
      if (resolvedTarget.kind === "aoe") cellTarget = resolvedTarget.cell;

      // Pre-resolve hits so VFX can fire first and damage can be deferred
      // (per-dart for magic_missile) to match the visual impact.
      const hits: { enemyId: string; damage: number }[] = [];
      for (const enemyId of enemyTargets) {
        const record = this.enemies.get(enemyId);
        if (!record || record.entity.hp <= 0) continue;

        let damage = 0;
        let resolved = false;
        // EV-E5: whether a rider condition lands (hit, or failed save).
        let condApplies = false;

        if (opts.spell.attackType === "auto") {
          damage = rollDiceNotation(damageDice);
          resolved = true;
          condApplies = true;
        } else if (opts.spell.attackType === "attack_roll") {
          const roll = resolveSpellAttack({
            spellcastingModifier: spellMod,
            proficiencyBonus: caster.proficiencyBonus,
            targetAC: record.entity.armorClass,
          });
          if (roll.hit) {
            damage = rollDiceNotation(damageDice);
            if (roll.isCriticalHit) damage += rollDiceNotation(damageDice);
            resolved = true;
            condApplies = true;
          }
        } else if (opts.spell.attackType === "saving_throw" && opts.spell.saveAbility) {
          const saveMod = abilityModifier(record.entity.abilities[opts.spell.saveAbility]);
          const save = resolveSavingThrow({
            dc,
            targetSaveModifier: saveMod,
            halfOnSave: opts.spell.halfOnSave,
          });
          const raw = rollDiceNotation(damageDice);
          if (save.outcome === "full") damage = raw;
          else if (save.outcome === "half") damage = Math.floor(raw / 2);
          resolved = true;
          condApplies = save.outcome === "full";
        }

        if (resolved && damage > 0) {
          hits.push({ enemyId, damage });
          targets.push(enemyId);
        }

        const cond = opts.spell.appliesCondition;
        if (cond && condApplies && record.entity.hp > 0) {
          applyConditionTo(record.entity.instanceId, record.entity.name, cond.id, {
            sourceId: caster.instanceId,
            ...(cond.durationSec !== undefined ? { durationSec: cond.durationSec } : {}),
          });
          if (!targets.includes(enemyId)) targets.push(enemyId);
        }
      }

      const applyHit = (enemyId: string, damage: number): void => {
        const record = this.enemies.get(enemyId);
        if (!record || record.entity.hp <= 0) return;
        const applied = applyDamage(record.entity, damage, damageType);
        record.entity.hp = applied.newHp;
        record.actor.send({ type: "TAKE_DAMAGE", amount: applied.appliedAmount });
        wakeOnDamage(record.entity.instanceId);
        EventBus.emit("combat:damage_taken", {
          instanceId: record.entity.instanceId,
          amount: applied.appliedAmount,
          damageType: damageType as DamageType,
        });
        if (record.entity.hp <= 0) this.onEnemyDied(record);
      };

      // Magic Missile: stagger damage to align with each dart's impact
      // (matches SpellVfx.addMissile: dart i fires at i*100ms, flight 500ms).
      if (opts.spell.id === "magic-missile" && hits.length > 0) {
        hits.forEach((h, i) => {
          setTimeout(() => applyHit(h.enemyId, h.damage), i * 100 + 500);
        });
      } else {
        for (const h of hits) applyHit(h.enemyId, h.damage);
      }

      pushFeed(
        targets.length > 0
          ? `${caster.name} casts ${opts.spell.name}.`
          : `${caster.name}'s ${opts.spell.name} misses.`,
      );
    } else if (opts.spell.appliesCondition) {
      // EV-E5: pure buff/debuff spells (Bless, Haste, Hold Person, Sleep).
      const cond = opts.spell.appliesCondition;
      const condOpts = {
        sourceId: caster.instanceId,
        ...(cond.durationSec !== undefined ? { durationSec: cond.durationSec } : {}),
      };

      if (opts.spell.targetTeam === "ally") {
        const allyIds: string[] =
          resolvedTarget.kind === "multi"
            ? [...resolvedTarget.instanceIds]
            : resolvedTarget.kind === "single" || resolvedTarget.kind === "touch"
              ? [resolvedTarget.instanceId]
              : resolvedTarget.kind === "self"
                ? [resolvedTarget.casterId]
                : [];
        for (const allyId of allyIds) {
          const ally = PartyStore.getMember(allyId);
          if (!ally || ally.hp <= 0) continue;
          applyConditionTo(allyId, ally.name, cond.id, condOpts);
          targets.push(allyId);
        }
        pushFeed(`${caster.name} casts ${opts.spell.name}.`);
      } else {
        const enemyTargets = this.expandDamageTargets(opts.spell, resolvedTarget);
        if (resolvedTarget.kind === "aoe") cellTarget = resolvedTarget.cell;
        for (const enemyId of enemyTargets) {
          const record = this.enemies.get(enemyId);
          if (!record || record.entity.hp <= 0) continue;
          let lands = true;
          if (opts.spell.attackType === "saving_throw" && opts.spell.saveAbility) {
            const saveMod = abilityModifier(record.entity.abilities[opts.spell.saveAbility]);
            const save = resolveSavingThrow({ dc, targetSaveModifier: saveMod, halfOnSave: false });
            lands = save.outcome === "full";
          }
          if (lands) {
            applyConditionTo(record.entity.instanceId, record.entity.name, cond.id, condOpts);
            targets.push(enemyId);
          } else {
            pushFeed(`${record.entity.name} resists.`);
          }
        }
        pushFeed(`${caster.name} casts ${opts.spell.name}.`);
      }
    }

    beginSpellCooldown(cooldownClock, caster.instanceId, opts.spell.level, opts.slotLevel);
    // Also lock the player's ATB attack timer for the same duration so the
    // PartyBar gauge visibly drains after a cast and weapon-attacks can't
    // fire immediately afterward. Without this, the ATB bar refills the
    // instant a spell goes out and the caster looks ready to swing.
    cooldownClock.register(
      playerAtbTimerId(caster.instanceId),
      spellCooldownSeconds(opts.spell.level, opts.slotLevel),
      () => {},
    );
    EventBus.emit("combat:spell_cast", {
      casterId: caster.instanceId,
      spellId: opts.spell.id,
      slotLevel: opts.slotLevel,
      targets,
      cellTarget,
    });
  }

  /**
   * Auto-target heuristic for spells the chooser invoked without going
   * through the target picker (S23). Damage spells default to the
   * nearest living enemy; heals default to the most-wounded ally
   * (caster included).
   */
  private autoTarget(spell: Spell, caster: PlayerCharacter): ResolvedTarget | null {
    if (spell.heal) {
      const members = PartyStore.getMembers().filter((m) => m.hp > 0);
      let pick: PlayerCharacter | null = null;
      let worst = Infinity;
      for (const m of members) {
        const ratio = m.hp / m.maxHp;
        if (ratio < worst) {
          worst = ratio;
          pick = m;
        }
      }
      if (!pick) return null;
      return spell.targeting === "self"
        ? { kind: "self", casterId: caster.instanceId }
        : { kind: "touch", instanceId: pick.instanceId };
    }
    // Damage: nearest living enemy.
    let closest: string | null = null;
    let closestDist = Infinity;
    for (const [id, record] of this.enemies) {
      if (record.entity.hp <= 0) continue;
      const d = manhattan(record.entity.position, this.playerPosition);
      if (d < closestDist) {
        closestDist = d;
        closest = id;
      }
    }
    if (!closest) return null;
    if (spell.targeting === "multi") {
      return { kind: "multi", instanceIds: [closest] };
    }
    return { kind: "single", instanceId: closest };
  }

  /**
   * Translate a `ResolvedTarget` into the list of enemy ids the spell
   * should damage. Multi splays to N nearest (Magic Missile darts);
   * AoE picks every enemy within 1 cell of the target cell (rough
   * 20-foot radius proxy). Single/touch are pass-through.
   */
  private expandDamageTargets(spell: Spell, target: ResolvedTarget): readonly string[] {
    if (target.kind === "single" || target.kind === "touch") {
      return [target.instanceId];
    }
    if (target.kind === "multi") {
      // Magic Missile: 3 darts at base level 1; +1 per upcast slot.
      // If the picker (or autoTarget) supplied explicit ids, honor them —
      // dump every dart on the chosen enemy(s). Only splay to nearest when
      // the caller passed an empty list, which shouldn't happen via the
      // current call sites but stays defensive.
      const dartCount = spell.id === "magic-missile" ? 3 : Math.max(1, target.instanceIds.length);
      if (target.instanceIds.length > 0) {
        const out: string[] = [];
        for (let i = 0; i < dartCount; i++) {
          const id = target.instanceIds[i % target.instanceIds.length]!;
          out.push(id);
        }
        return out;
      }
      const enemies = [...this.enemies.entries()]
        .filter(([, r]) => r.entity.hp > 0)
        .sort(
          ([, a], [, b]) =>
            manhattan(a.entity.position, this.playerPosition) -
            manhattan(b.entity.position, this.playerPosition),
        );
      const out: string[] = [];
      for (let i = 0; i < dartCount; i++) {
        const pick = enemies[i % Math.max(1, enemies.length)];
        if (pick) out.push(pick[0]);
      }
      return out;
    }
    if (target.kind === "aoe") {
      const out: string[] = [];
      for (const [id, r] of this.enemies) {
        if (r.entity.hp <= 0) continue;
        const dx = Math.abs(r.entity.position.x - target.cell.x);
        const dz = Math.abs(r.entity.position.z - target.cell.z);
        if (dx <= 1 && dz <= 1) out.push(id);
      }
      return out;
    }
    return [];
  }

  /**
   * Snapshot of every registered enemy in the shape `checkCampEligibility`
   * consumes (C08 wiring) — position, faction, and liveness only.
   */
  getCampEntities(): { position: Position; faction: EnemyFaction; alive: boolean }[] {
    return [...this.enemies.values()].map((r) => ({
      position: { x: r.entity.position.x, z: r.entity.position.z },
      faction: r.entity.faction,
      alive: r.entity.hp > 0,
    }));
  }

  // ── Game loop ─────────────────────────────────────────────────────────────

  tick(delta: number): void {
    // EV-E4: advance condition durations / DoT ticks before actions so an
    // expiring Sleep or a lethal Burning tick lands ahead of this frame's
    // attacks.
    this.tickConditions(delta);

    let anyAlive = false;
    let engagedCount = 0;
    // Pack aggro: enemies that crossed the proximity threshold this tick.
    // After the scan they rouse hostile allies within the allies' own
    // hearing radius (one hop — woken allies don't re-propagate).
    const packSources: EnemyRecord[] = [];

    for (const record of this.enemies.values()) {
      if (record.entity.hp <= 0) continue;
      anyAlive = true;

      const dist = manhattan(record.entity.position, this.playerPosition);
      const snap = record.actor.getSnapshot();

      if (snap.matches("combat") || snap.matches("attacking")) {
        engagedCount++;
      }

      const watchful = snap.matches("alert") || snap.matches("alerted");

      // Proximity detection: idle/patrolling/alert/alerted → combat.
      // Watchful states MUST be detection-eligible — an alert enemy that
      // can't re-spot the player is a statue. Peace mode gates detection
      // entirely — and with it pack aggro, whose sources come from here.
      if (
        !isPeaceMode() &&
        dist <= DETECTION_RANGE &&
        (snap.matches("idle") || snap.matches("patrolling") || watchful)
      ) {
        record.actor.send({ type: "PLAYER_DETECTED" });
        this.spriteMgr?.faceToward(record.entity.instanceId, this.playerPosition);
        EventBus.emit("enemy:detected", {
          instanceId: record.entity.instanceId,
          name: record.entity.name,
        });
        // E05: startle timer on first COMBAT transition. First contact is
        // longer than re-engagement; uses the seeded RNG for determinism.
        record.actionTimer = startleTimerSeconds({
          firstContact: !record.hasEverDetected,
          rand: randomFloat01,
        });
        record.hasEverDetected = true;
        if (record.entity.faction === "hostile") packSources.push(record);
        if (!this.inCombat) {
          this.inCombat = true;
          this.endWatcher.reset();
          gameActor.send({ type: "COMBAT_STARTED" });
        }
        engagedCount++;
      } else if (
        (snap.matches("idle") || snap.matches("patrolling")) &&
        record.entity.leashRadius > 0 &&
        this.gridMap
      ) {
        // E06: gravity-point patrol — step within leashRadius of the anchor.
        record.patrolTimer -= delta;
        if (record.patrolTimer <= 0) {
          record.patrolTimer = PATROL_STEP_INTERVAL;
          // EV-E4: sleeping/paralyzed enemies don't wander. Peace mode:
          // a wounded enemy stands still — patrolling targets that walk
          // out from under the cursor make map-cleanup swings a chase.
          const woundedInPeace = isPeaceMode() && record.entity.hp < record.entity.maxHp;
          if (!isIncapacitated(record.entity.instanceId) && !woundedInPeace) {
            this.stepEnemyPatrol(record);
          }
        }
      } else if (watchful) {
        // E04: alert states calm back down to idle after a watchful
        // window — the machine defines the timeout transitions but the
        // manager is the only clock that can fire them.
        record.alertTimer -= delta;
        if (record.alertTimer <= 0) {
          record.actor.send({
            type: snap.matches("alerted") ? "ALERTED_TIMEOUT" : "ALERT_TIMEOUT",
          });
        }
      }

      // Engaged enemies track the player with their facing — a stationary
      // boss (xanathar, leash 0) otherwise attacks while showing the
      // camera whatever side it happened to spawn with.
      if (snap.matches("combat") || snap.matches("attacking")) {
        this.spriteMgr?.faceToward(record.entity.instanceId, this.playerPosition);
      }

      // ATB countdown — only tick while in combat state
      if (snap.matches("combat")) {
        // B08: flee detection — give up the chase when out of leash range
        // OR the pathfinder can't reach the player at all (walled off).
        // Peace mode reuses the same stand-down path so enemies engaged
        // at toggle time walk it back through alert → idle.
        const unreachable =
          !isPeaceMode() &&
          this.gridMap !== null &&
          findPath(this.gridMap, record.entity.position, this.playerPosition).length === 0;
        if (isPeaceMode() || dist > FLEE_DISTANCE || unreachable) {
          record.actor.send({ type: "PLAYER_LOST" });
          record.alertTimer = ALERT_CALM_SECONDS;
          engagedCount--;
          continue;
        }

        // EV-E1 range + line of sight gate every hostile act. LOS matters
        // for melee too: manhattan-adjacent cells can still have a razor
        // wall between them, and enemies used to swing through it — the
        // same wall-ignoring bug the party's ranged hand buttons had.
        const attackRange = record.entity.attack?.ranged
          ? Math.max(1, record.entity.attack.range)
          : 1;
        const inRange = dist <= attackRange;
        const visible =
          this.gridMap === null ||
          hasLineOfSight(this.gridMap, record.entity.position, this.playerPosition);
        const incapacitated = isIncapacitated(record.entity.instanceId);

        // Movement runs on its own cadence — closing distance no longer
        // spends the attack action (and attacking doesn't stall the chase).
        record.moveTimer -= delta;
        if (record.moveTimer <= 0) {
          record.moveTimer =
            ENEMY_MOVE_COOLDOWN * conditionCooldownScale(record.entity.instanceId);
          if (!incapacitated && (!inRange || !visible) && this.gridMap) {
            this.stepEnemyTowardPlayer(record);
          }
        }

        record.actionTimer -= delta;
        if (record.actionTimer <= 0) {
          // EV-E4: incapacitated enemies (Sleep, paralysis, …) lose the
          // action and re-check shortly instead of banking a full cooldown.
          if (incapacitated) {
            record.actionTimer = 0.5;
            continue;
          }
          // Attack ready but target unseen: hold at the grace clamp so
          // arrival telegraphs instead of instant-hitting.
          if (!visible) {
            record.actionTimer = ATTACK_READY_GRACE;
            continue;
          }
          // EV-E3: casters with a loaded, in-range spell cast about half
          // the time; otherwise fall through to the weapon logic.
          const spell = this.pickEnemySpell(record, dist);
          if (!spell && !inRange) {
            record.actionTimer = ATTACK_READY_GRACE;
            continue;
          }
          record.actionDuration =
            scaleEnemyCooldown(ENEMY_ACTION_COOLDOWN) *
            conditionCooldownScale(record.entity.instanceId);
          record.actionTimer = record.actionDuration;

          if (spell) {
            record.actor.send({ type: "ATTACK_COMMAND" });
            this.castEnemySpell(record, spell);
          } else {
            record.actor.send({ type: "ATTACK_COMMAND" });
            this.executeEnemyAttack(record);
          }
        }
      }
    }

    // Pack aggro propagation — a detected hostile rouses hostile allies
    // whose own hearing radius reaches it. One feed line per source via
    // enemy:pack_alerted (no per-ally "notices you" spam).
    for (const src of packSources) {
      const woken: string[] = [];
      for (const ally of this.enemies.values()) {
        if (ally === src || ally.entity.hp <= 0) continue;
        if (ally.entity.faction !== "hostile") continue;
        const snap = ally.actor.getSnapshot();
        const eligible =
          snap.matches("idle") ||
          snap.matches("patrolling") ||
          snap.matches("alert") ||
          snap.matches("alerted");
        if (!eligible) continue;
        if (manhattan(ally.entity.position, src.entity.position) > ally.entity.hearing.radius)
          continue;
        ally.actor.send({ type: "PLAYER_DETECTED" });
        this.spriteMgr?.faceToward(ally.entity.instanceId, this.playerPosition);
        ally.actionTimer = startleTimerSeconds({
          firstContact: !ally.hasEverDetected,
          rand: randomFloat01,
        });
        ally.hasEverDetected = true;
        woken.push(ally.entity.instanceId);
        engagedCount++;
      }
      if (woken.length > 0) {
        EventBus.emit("enemy:pack_alerted", {
          byInstanceId: src.entity.instanceId,
          byName: src.entity.name,
          instanceIds: woken,
        });
      }
    }

    // Transition back to exploring once all enemies are dead
    if (this.inCombat && !anyAlive) {
      this.inCombat = false;
      this.endWatcher.reset();
      gameActor.send({ type: "COMBAT_ENDED" });
      pushFeed(formatCombatEnded());
      this.cleanseAllOn("combatEnd");
      return;
    }

    // B09: quiet-window fallback — enemies still alive but none engaged
    // (player walked out of range, enemies fled). Ends combat after the
    // window elapses without an engaged enemy.
    if (this.inCombat && this.endWatcher.tick(delta, engagedCount)) {
      this.inCombat = false;
      gameActor.send({ type: "COMBAT_ENDED" });
      pushFeed(formatCombatEnded());
      this.cleanseAllOn("combatEnd");
    }
  }

  dispose(): void {
    // E15: occupancy is shared — only remove this manager's own enemies
    // so NPCs (registered by SpriteManager) and any future non-combat
    // occupants stay valid until their owner tears down.
    for (const id of this.enemies.keys()) this.occupancy.remove(id);
    this.enemies.clear();
    // B11: drop any in-flight player attack cooldowns so a fresh map starts
    // every party member ready.
    for (const m of PartyStore.getMembers()) {
      cooldownClock.unregister(playerAtbTimerId(m.instanceId));
    }
    this.playerActor = null;
    this.spriteMgr = null;
    this.gridMap = null;
  }

  // ── ATB readouts (for B07 HUD) ────────────────────────────────────────────

  /**
   * Returns the ATB state for a single enemy, or null if it is unregistered
   * or already dead. `engaged` is true while the enemy is in COMBAT or
   * ATTACKING — the HUD hides the floating meter on anything else.
   */
  getEnemyAtbState(instanceId: string): EnemyAtbSnapshot | null {
    const record = this.enemies.get(instanceId);
    if (!record || record.entity.hp <= 0) return null;
    const snap = record.actor.getSnapshot();
    const engaged = snap.matches("combat") || snap.matches("attacking");
    return {
      instanceId,
      remaining: Math.max(0, record.actionTimer),
      duration: ENEMY_ACTION_COOLDOWN,
      engaged,
      faction: record.entity.faction,
      worldPos: this.readSpritePos(instanceId),
    };
  }

  /**
   * Enumerates every currently engaged enemy — used by `EnemyMeter` to
   * decide which floating cooldown bars to render. Returns a fresh array
   * on every call; callers should not retain it across frames.
   */
  /**
   * Display names of every registered enemy on the current map (alive or
   * dead). Feeds the combat-log highlighter so monster names colorize —
   * presentation only, no combat state leaks through this.
   */
  listEnemyNames(): readonly string[] {
    const names = new Set<string>();
    for (const record of this.enemies.values()) names.add(record.entity.name);
    return [...names];
  }

  listEngagedEnemies(): readonly EnemyAtbSnapshot[] {
    const out: EnemyAtbSnapshot[] = [];
    for (const record of this.enemies.values()) {
      if (record.entity.hp <= 0) continue;
      const snap = record.actor.getSnapshot();
      if (!(snap.matches("combat") || snap.matches("attacking"))) continue;
      // The meter is an attack telegraph, not a radar: it shows only when
      // the enemy could actually strike — within its attack range AND with
      // line of sight (an engaged enemy pathing behind a wall must not
      // paint its position through the geometry). Movement has no meter.
      const meterRange = record.entity.attack?.ranged
        ? Math.max(1, record.entity.attack.range)
        : 1;
      if (manhattan(record.entity.position, this.playerPosition) > meterRange) continue;
      if (
        this.gridMap !== null &&
        !hasLineOfSight(this.gridMap, record.entity.position, this.playerPosition)
      ) {
        continue;
      }
      out.push({
        instanceId: record.entity.instanceId,
        remaining: Math.max(0, record.actionTimer),
        duration: record.actionDuration,
        engaged: true,
        faction: record.entity.faction,
        worldPos: this.readSpritePos(record.entity.instanceId),
      });
    }
    return out;
  }

  /**
   * B11 — returns the live ATB state for a player character. `remaining`
   * comes from `cooldownClock`; 0 means the character is ready to swing.
   * `duration` reports the effective cooldown so the HUD fill ratio
   * stays correct under future DEX/Haste/Slow modifiers.
   */
  getPlayerAtbState(instanceId: string): AtbSnapshot {
    const member = PartyStore.getMembers().find((m) => m.instanceId === instanceId);
    const duration = member ? effectivePlayerCooldown(member) : PLAYER_ACTION_COOLDOWN;
    return {
      remaining: cooldownClock.remaining(playerAtbTimerId(instanceId)),
      duration,
    };
  }

  /** True while any enemy has transitioned to COMBAT/ATTACKING. */
  isInCombat(): boolean {
    return this.inCombat;
  }

  /**
   * S26 — minimap targeting reads this to paint enemy markers. Returns a
   * snapshot of every registered enemy (including dead ones — the picker
   * needs them for revive-class spells). Callers must not retain across
   * frames.
   */
  enemiesSnapshot(): readonly {
    instanceId: string;
    name: string;
    position: Position;
    hp: number;
    maxHp: number;
    faction: EnemyFaction;
  }[] {
    const out: {
      instanceId: string;
      name: string;
      position: Position;
      hp: number;
      maxHp: number;
      faction: EnemyFaction;
    }[] = [];
    for (const record of this.enemies.values()) {
      const e = record.entity;
      out.push({
        instanceId: e.instanceId,
        name: e.name,
        position: e.position,
        hp: e.hp,
        maxHp: e.maxHp,
        faction: e.faction,
      });
    }
    return out;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private readSpritePos(
    instanceId: string,
  ): { readonly x: number; readonly y: number; readonly z: number } | null {
    const pos = this.spriteMgr?.getSpritePos(instanceId);
    if (!pos) return null;
    return { x: pos.x, y: pos.y, z: pos.z };
  }

  // ── E-pack: size-aware co-occupancy ────────────────────────────────────────

  /**
   * Sizes of everything sharing `pos` (excluding the mover itself).
   * Occupants without an enemy record — NPCs, mostly — resolve to `null`,
   * which `canJoinCell` treats as always-blocking.
   */
  private occupantSizes(pos: Position, excludeId: string): Array<MonsterSize | null> {
    return this.occupancy
      .at(pos)
      .filter((id) => id !== excludeId)
      .map((id) => this.enemies.get(id)?.entity.size ?? null);
  }

  private canEnterCell(entity: MonsterEntity, pos: Position): boolean {
    return canJoinCell(entity.size, this.occupantSizes(pos, entity.instanceId));
  }

  /**
   * Reassign 2×2 sub-cell corners for every enemy sharing `cell` so packs
   * spread with width AND depth on screen. Sorted by instanceId for a
   * deterministic layout; a lone survivor snaps back to dead center.
   */
  private syncCellSlots(cell: Position): void {
    const ids = this.occupancy
      .at(cell)
      .filter((id) => this.enemies.has(id))
      .sort();
    for (let i = 0; i < ids.length; i++) {
      this.spriteMgr?.setSubCellSlot(ids[i]!, i, ids.length);
    }
  }

  /** Test hook — drive a slot resync without a full damage/death cycle. */
  syncCellSlotsForTest(cell: Position): void {
    this.syncCellSlots(cell);
  }

  /**
   * Live world-space sprite position of any registered enemy (sub-cell
   * pack offsets included), or null when unknown. Feeds the floating
   * damage-number layer, which follows sprites rather than cells.
   */
  getEnemyWorldPos(
    instanceId: string,
  ): { readonly x: number; readonly y: number; readonly z: number } | null {
    if (!this.enemies.has(instanceId)) return null;
    return this.readSpritePos(instanceId);
  }

  private moveEnemyTo(record: EnemyRecord, nextStep: Position): void {
    const from = { ...record.entity.position };
    this.occupancy.place(record.entity.instanceId, nextStep);
    record.entity.position = nextStep;
    this.spriteMgr?.moveTo(record.entity.instanceId, nextStep);
    this.syncCellSlots(from);
    this.syncCellSlots(nextStep);
    record.actor.send({ type: "MOVE", target: nextStep });
  }

  private stepEnemyPatrol(record: EnemyRecord): void {
    if (!this.gridMap) return;
    const nextStep = chooseNextPatrolStep({
      current: record.entity.position,
      anchor: record.entity.anchor,
      leashRadius: record.entity.leashRadius,
      grid: this.gridMap,
      isCellFree: (pos) => this.canEnterCell(record.entity, pos),
    });
    if (!nextStep) return;
    this.moveEnemyTo(record, nextStep);
  }

  private stepEnemyTowardPlayer(record: EnemyRecord): void {
    if (!this.gridMap) return;

    const path = findPath(this.gridMap, record.entity.position, this.playerPosition);
    if (path.length === 0) return;

    const nextStep = path[0]!;
    // E-pack: mediums keep the one-per-cell rule; smalls stack up to
    // PACK_MAX with other smalls. Hold position when the target cell
    // can't take us — a stall is still correct for blocked movers.
    if (!this.canEnterCell(record.entity, nextStep)) return;
    this.moveEnemyTo(record, nextStep);
  }

  private executeEnemyAttack(record: EnemyRecord): void {
    const members = PartyStore.getMembers();
    const alive = members.filter((m) => m.hp > 0);
    if (alive.length === 0) {
      record.actor.send({ type: "ANIMATION_COMPLETE" });
      return;
    }

    const profile = getEnemyAttackProfile(record.entity);
    const targetIds = selectAttackTargets(profile, toTargetingMembers(members));
    if (targetIds.length === 0) {
      record.actor.send({ type: "ANIMATION_COMPLETE" });
      return;
    }

    for (const id of targetIds) {
      const target = members.find((m) => m.instanceId === id);
      if (!target || target.hp <= 0) continue;
      this.resolveEnemyAttackAgainst(record, target);
    }

    record.actor.send({ type: "ANIMATION_COMPLETE" }); // ATTACKING → COMBAT
  }

  private resolveEnemyAttackAgainst(record: EnemyRecord, target: PlayerCharacter): void {
    // EV-E1: the authored attack block drives dice, damage type, and the
    // to-hit/damage stat. Absent block = the legacy 1d6 slashing STR melee.
    const atk = record.entity.attack ?? null;
    const abilityMod = abilityModifier(
      atk?.ability === "DEX" ? record.entity.abilities.DEX : record.entity.abilities.STR,
    );
    const mods = aggregateAttackModifiers({
      attacker: entityConditions(record.entity),
      defender: entityConditions(target),
    });

    const attackRoll = resolveAttackRoll({
      abilityModifier: abilityMod,
      proficiencyBonus: 2,
      targetAC: target.armorClass,
      hitBonus: mods.hitBonus,
    });

    const combatEvent: CombatEvent = {
      attackerId: record.entity.instanceId,
      targetId: target.instanceId,
      attackRoll,
      damage: null,
      hit: attackRoll.hit,
      timestamp: Date.now(),
    };

    if (attackRoll.hit) {
      const dmg = resolveDamage({
        diceCount: atk?.diceCount ?? 1,
        diceSides: atk?.diceSides ?? 6,
        modifier: abilityMod + (atk?.modifier ?? 0) + mods.damageBonus,
        damageType: atk?.damageType ?? "slashing",
        isCriticalHit: attackRoll.isCriticalHit,
      });
      // Difficulty scaling — enemy-side only, applied post-roll so the
      // feed, the reaction window, and the HP change all agree.
      const scaledDmg = { ...dmg, total: scaleEnemyDamage(dmg.total) };

      const applied = applyDamage(target, scaledDmg.total, scaledDmg.damageType);
      PartyStore.updateMember(target.instanceId, { hp: applied.newHp });
      // Legacy single-player actor: do NOT forward damage. Its shadow HP
      // (hardcoded 30) fed a final `dead` state after ~30 cumulative party
      // damage, and the input gate then rejected all movement forever —
      // the "party frozen after combat" bug. Party HP lives in PartyStore;
      // party death is partyWipe's domain.
      wakeOnDamage(target.instanceId);
      EventBus.emit("combat:damage_taken", {
        instanceId: target.instanceId,
        amount: applied.appliedAmount,
        damageType: scaledDmg.damageType,
      });

      // EV-E4: chance-based condition rider from the attack block.
      const rider = atk?.conditionOnHit;
      if (rider && applied.newHp > 0 && randomFloat01() < rider.chance) {
        applyConditionTo(target.instanceId, target.name, rider.id, {
          sourceId: record.entity.instanceId,
        });
      }

      pushFeed(
        formatAttackHit(
          record.entity.name,
          target.name,
          scaledDmg.total,
          scaledDmg.damageType,
          attackRoll.isCriticalHit,
        ),
      );

      (combatEvent as { -readonly [K in keyof CombatEvent]: CombatEvent[K] }).damage = scaledDmg;

      // Open a reaction window so the player can use Shield, Parry, etc.
      openReactionWindow({
        eventId: uid(),
        type: "reaction_window",
        attackEvent: combatEvent,
      });

      // B05: match enabled automatic reactions on the defender. Fatal
      // triggers fire in addition to on_hit_taken / on_crit_taken so a
      // single event can surface multiple reactions.
      this.fireReactions(target, "on_hit_taken");
      if (attackRoll.isCriticalHit) this.fireReactions(target, "on_crit_taken");
      if (applied.newHp <= 0) this.fireReactions(target, "on_fatal_damage");
    } else {
      pushFeed(formatAttackMiss(record.entity.name, target.name, false));
      this.fireReactions(target, "on_miss_taken");
    }

    EventBus.emit("combat:attack", combatEvent);
  }

  /**
   * B05: match the defender's enabled reactions against `trigger` and emit
   * `combat:reaction_matched` when any fire. Reaction effects themselves
   * are resolved by downstream tasks (S10 etc.); this hook exists so the
   * UI layer can already surface matched reactions in v1.
   */
  private fireReactions(defender: PlayerCharacter, trigger: ReactionTrigger): void {
    const reactions = defender.reactions;
    const characterClass = defender.characterClass;
    if (!reactions || reactions.length === 0) return;
    const matched = matchReactions({ reactions, characterClass }, trigger);
    if (matched.length === 0) return;
    EventBus.emit("combat:reaction_matched", {
      defenderId: defender.instanceId,
      trigger,
      reactionIds: matched,
    });
  }

  // ── EV-E3/E4: enemy casting + condition runtime ───────────────────────────

  /** Resolved spell kits per dataId — enemy casters of a type share one. */
  private readonly enemySpellCache = new Map<string, Spell[]>();

  private async warmEnemySpells(dataId: string, ids: readonly string[]): Promise<void> {
    if (this.enemySpellCache.has(dataId)) return;
    // Reserve so concurrent spawns don't double-fetch; only commit the
    // final list if our reservation is still current (a direct seed —
    // tests, future preloads — must not be clobbered by this async tail).
    const reservation: Spell[] = [];
    this.enemySpellCache.set(dataId, reservation);
    const spells: Spell[] = [];
    for (const id of ids) {
      const spell = await loadSpell(id);
      if (spell) spells.push(spell);
      else console.warn(`[CombatManager] enemy spell "${id}" not found for ${dataId}`);
    }
    if (this.enemySpellCache.get(dataId) === reservation) {
      this.enemySpellCache.set(dataId, spells);
    }
  }

  /** Test seam: seed the resolved spell cache without a network fetch. */
  _seedEnemySpells(dataId: string, spells: Spell[]): void {
    this.enemySpellCache.set(dataId, spells);
  }

  /** About half of READY actions cast, when a loaded spell is in range. */
  private pickEnemySpell(record: EnemyRecord, dist: number): Spell | null {
    if (record.entity.spells.length === 0) return null;
    const spells = this.enemySpellCache.get(record.entity.dataId) ?? [];
    const castable = spells.filter((sp) => dist <= Math.max(1, sp.rangeCells));
    if (castable.length === 0) return null;
    if (randomFloat01() >= 0.5) return null;
    return castable[Math.floor(randomFloat01() * castable.length)] ?? null;
  }

  /**
   * EV-E3 — resolve an enemy cast against the party through the shared
   * spell pipeline: attack-roll and save-based spells, damage (difficulty-
   * scaled like weapon hits), and save-gated conditions. Enemies have no
   * slot economy (enemies-and-ai: infinite casting, paced by the ATB).
   */
  private castEnemySpell(record: EnemyRecord, spell: Spell): void {
    const members = PartyStore.getMembers();
    const shape = spell.targeting === "aoe" ? ("aoe" as const) : ("spell-single" as const);
    const targetIds = selectAttackTargets({ shape }, toTargetingMembers(members));
    if (targetIds.length === 0) {
      record.actor.send({ type: "ANIMATION_COMPLETE" });
      return;
    }
    const castMod = abilityModifier(record.entity.abilities.INT);
    const dc = spellSaveDc(castMod, 2);
    pushFeed(`${record.entity.name} casts ${spell.name}!`);

    for (const id of targetIds) {
      const target = members.find((m) => m.instanceId === id);
      if (!target || target.hp <= 0) continue;

      let damage = 0;
      let effectLands = true;
      if (spell.attackType === "attack_roll") {
        const roll = resolveSpellAttack({
          spellcastingModifier: castMod,
          proficiencyBonus: 2,
          targetAC: target.armorClass,
        });
        effectLands = roll.hit;
        if (roll.hit && spell.damage) {
          damage = rollDiceNotation(spell.damage.dice);
          if (roll.isCriticalHit) damage += rollDiceNotation(spell.damage.dice);
        }
      } else if (spell.attackType === "saving_throw" && spell.saveAbility) {
        const saveProfs = (target as { saveProficiencies?: ReadonlySet<string> })
          .saveProficiencies;
        const saveMod =
          abilityModifier(target.abilities[spell.saveAbility]) +
          (saveProfs?.has(spell.saveAbility) ? target.proficiencyBonus : 0);
        const save = resolveSavingThrow({
          dc,
          targetSaveModifier: saveMod,
          halfOnSave: spell.halfOnSave,
        });
        effectLands = save.outcome === "full";
        if (spell.damage) {
          const raw = rollDiceNotation(spell.damage.dice);
          damage = save.outcome === "full" ? raw : save.outcome === "half" ? Math.floor(raw / 2) : 0;
        }
      } else if (spell.damage) {
        damage = rollDiceNotation(spell.damage.dice);
      }

      if (damage > 0 && spell.damage) {
        const applied = applyDamage(target, scaleEnemyDamage(damage), spell.damage.type);
        PartyStore.updateMember(target.instanceId, { hp: applied.newHp });
        // Legacy single-player actor: do NOT forward damage. Its shadow HP
      // (hardcoded 30) fed a final `dead` state after ~30 cumulative party
      // damage, and the input gate then rejected all movement forever —
      // the "party frozen after combat" bug. Party HP lives in PartyStore;
      // party death is partyWipe's domain.
        wakeOnDamage(target.instanceId);
        EventBus.emit("combat:damage_taken", {
          instanceId: target.instanceId,
          amount: applied.appliedAmount,
          damageType: spell.damage.type,
        });
        pushFeed(`${target.name} takes ${applied.appliedAmount} ${spell.damage.type} damage.`);
        this.fireReactions(target, "on_hit_taken");
        if (applied.newHp <= 0) this.fireReactions(target, "on_fatal_damage");
      } else if (!effectLands) {
        pushFeed(`${target.name} resists.`);
      }

      if (spell.appliesCondition && effectLands) {
        applyConditionTo(target.instanceId, target.name, spell.appliesCondition.id, {
          sourceId: record.entity.instanceId,
          ...(spell.appliesCondition.durationSec !== undefined
            ? { durationSec: spell.appliesCondition.durationSec }
            : {}),
        });
      }
    }
    record.actor.send({ type: "ANIMATION_COMPLETE" });
  }

  /** EV-E4 — advance conditions for party + enemies; apply DoT emissions. */
  private tickConditions(delta: number): void {
    for (const member of PartyStore.getMembers()) {
      if (member.hp <= 0) {
        conditionManager.clearAll(member.instanceId);
        continue;
      }
      for (const em of conditionManager.tick(member.instanceId, delta)) {
        const fresh = PartyStore.getMember(member.instanceId);
        if (!fresh || fresh.hp <= 0) break;
        const applied = applyDamage(fresh, em.amount, em.damageType);
        PartyStore.updateMember(fresh.instanceId, { hp: applied.newHp });
        // Legacy single-player actor: do NOT forward damage. Its shadow HP
      // (hardcoded 30) fed a final `dead` state after ~30 cumulative party
      // damage, and the input gate then rejected all movement forever —
      // the "party frozen after combat" bug. Party HP lives in PartyStore;
      // party death is partyWipe's domain.
        EventBus.emit("combat:damage_taken", {
          instanceId: fresh.instanceId,
          amount: applied.appliedAmount,
          damageType: em.damageType,
        });
        pushFeed(
          `${fresh.name} takes ${applied.appliedAmount} ${em.damageType} damage (${em.conditionId}).`,
        );
      }
    }
    for (const record of this.enemies.values()) {
      if (record.entity.hp <= 0) continue;
      for (const em of conditionManager.tick(record.entity.instanceId, delta)) {
        if (record.entity.hp <= 0) break;
        const applied = applyDamage(record.entity, em.amount, em.damageType);
        record.entity.hp = applied.newHp;
        record.actor.send({ type: "TAKE_DAMAGE", amount: applied.appliedAmount });
        EventBus.emit("combat:damage_taken", {
          instanceId: record.entity.instanceId,
          amount: applied.appliedAmount,
          damageType: em.damageType,
        });
        if (record.entity.hp <= 0) this.onEnemyDied(record);
      }
    }
  }

  /** Cleanse trigger fan-out across the party and every live enemy. */
  private cleanseAllOn(trigger: CleanseTrigger): void {
    for (const m of PartyStore.getMembers()) conditionManager.cleanse(m.instanceId, trigger);
    for (const r of this.enemies.values()) conditionManager.cleanse(r.entity.instanceId, trigger);
  }

  private onEnemyDied(record: EnemyRecord): void {
    conditionManager.clearAll(record.entity.instanceId);
    EventBus.emit("combat:entity_died", { instanceId: record.entity.instanceId });
    // E11: feed line is now routed through the typed channel; see initFeedBus.
    EventBus.emit("enemy:died", {
      instanceId: record.entity.instanceId,
      name: record.entity.name,
    });
    // W06: record death on the active map so a re-entry skips the spawn.
    WorldStore.markEnemyDead(record.entity.instanceId);
    // E12: ask the runtime-owned resolver for a loot pile and stash it.
    if (this.lootResolver && record.entity.lootTableId) {
      const pile = this.lootResolver(record.entity);
      if (pile && pile.stacks.length > 0) {
        LootPileStore.put(pile);
        // Persist on the active map so re-entry (and saves) restore
        // uncollected drops — main.tsx rehydrates from WorldStore.
        WorldStore.addFloorDrop(
          pile.id,
          pile.position,
          pile.stacks.flatMap((s) => Array.from({ length: s.quantity }, () => s.item.id)),
        );
        EventBus.emit("world:loot_dropped", {
          pileId: pile.id,
          position: pile.position,
          source: record.entity.name,
        });
      }
    }
    // W07: award authored XP to the whole party.
    if (record.entity.xp > 0) {
      PartyStore.awardXp(record.entity.xp);
      EventBus.emit("world:xp_awarded", {
        amount: record.entity.xp,
        source: record.entity.instanceId,
      });
    }
    this.occupancy.remove(record.entity.instanceId);
    // E-pack: surviving cellmates spread back over the freed corners
    // (or re-center when only one remains).
    this.syncCellSlots(record.entity.position);

    // Prefer SpriteManager death animation; fall back to removing placeholder mesh
    if (this.spriteMgr) {
      this.spriteMgr.startDeathAnimation(record.entity.instanceId);
    } else if (record.placeholder?.parent) {
      record.placeholder.parent.remove(record.placeholder);
    }
    record.placeholder = null;
  }
}

// ── Attack-profile resolution ────────────────────────────────────────────────

export interface PlayerAttackOpts {
  /** Which party member is swinging. Defaults to first alive. */
  readonly attackerInstanceId?: string;
  /** Which hand the player tapped. Defaults to mainHand. */
  readonly hand?: EquipmentSlot;
}

interface AttackProfile {
  readonly diceCount: number;
  readonly diceSides: number;
  readonly damageType: DamageType;
  readonly ability: AbilityName;
}

/** Unarmed fallback when the chosen hand has no weapon (matches SRD strike). */
const UNARMED: AttackProfile = {
  diceCount: 1,
  diceSides: 4,
  damageType: "bludgeoning",
  ability: "STR",
};

function isWeapon(item: { category: string } | null): item is Weapon {
  return item?.category === "weapon";
}

/**
 * Pick the attack profile (dice + damage type + ability) for `attacker`'s
 * `hand` slot. Falls back to mainHand when `hand` is undefined; falls back
 * to an unarmed strike when the slot is empty or holds a non-weapon.
 *
 * Finesse weapons use `max(STR, DEX)`; everything else honors the weapon's
 * authored `abilityScore`.
 */
function resolveAttackProfile(
  attacker: PlayerCharacter,
  hand: EquipmentSlot | undefined,
): AttackProfile {
  const inv = InventoryStore.get(attacker.instanceId);
  const slot = hand ?? "mainHand";
  const equipped = inv.equipment[slot] ?? inv.equipment.mainHand;
  if (!isWeapon(equipped)) return UNARMED;

  const finesse = equipped.properties.includes("finesse");
  const ability: AbilityName = finesse
    ? abilityModifier(attacker.abilities.DEX) > abilityModifier(attacker.abilities.STR)
      ? "DEX"
      : "STR"
    : equipped.abilityScore;

  return {
    diceCount: equipped.damage.count,
    diceSides: equipped.damage.sides,
    damageType: equipped.damageType,
    ability,
  };
}
